import React from 'react'
import { WideHeader } from './components/header'
import { FixedFooter } from './components/footer'
import { useTranslation } from '../providers/translation'
import { Logo } from '../components/simple/logo'
import { IconHelp } from '../icons/help'
import { Button } from '../components/simple/button'
import { SpecialPanel } from './components/panel'

/**
 * A simple layout component that shows a header and a footer.
 * @param children The content of the layout
 * @param headerActions Extra actions to show in the header
 * @param footer Whether to show the footer. Defaults to true.
 * @param helpLink A link to a help page, which is shown as a question mark icon in the top right
 * @param verticalScroll Whether to allow vertical scrolling of the entire page. Defaults to true.
 * @param showName Whether to show the name of the app in the header. Defaults to true.
 */
export const SimpleLayout: React.FC<{
  children: React.ReactNode
  headerActions?: React.ReactNode
  footer?: boolean
  helpLink?: string
  verticalScroll?: boolean
  showName?: boolean
}> = ({ children, headerActions, footer = false, helpLink, verticalScroll = true, showName = true }) => {
  const { tr } = useTranslation()
  return (
    <div className={`flex flex-col ${verticalScroll ? '' : 'max-h-screen min-h-screen'}`}>
      <WideHeader>
        <Logo size="lg" link="/" showName={showName} />
        {headerActions}
        {helpLink && (
          <a href={helpLink} target="_blank" className="-mr-2 ml-auto text-gray-200 hover:text-gray-300 sm:-mr-8">
            <IconHelp size="xl" />
          </a>
        )}
      </WideHeader>
      {children}
      {footer && (
        <FixedFooter>
          <span>
            {tr('Reach us at')}{' '}
            <a href="mailto: contact@reachmyteach.com" className="text-white/40">
              contact@reachmyteach.com
            </a>
          </span>
          <a href="https://reachmyteach.zendesk.com/" className="text-white/40">
            {tr('Support and documentation')}
          </a>
        </FixedFooter>
      )}
    </div>
  )
}

/**
 * A simple page layout that shows a message.
 * @param children The content of the message
 * @param linkToHome Whether to show a button to go home
 */
export const SimpleMessage: React.FC<{ children: React.ReactNode; linkToHome?: boolean }> = ({
  children,
  linkToHome,
}) => {
  return (
    <SimpleLayout footer={true}>
      <SpecialPanel>
        {children}
        {linkToHome && <Button link="/">Go Home</Button>}
      </SpecialPanel>
    </SimpleLayout>
  )
}
